import React from 'react';
import { motion } from 'framer-motion';
import { Users, Star, FileCheck, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { useAdminStore } from '@/lib/stores/admin-store';

export function AdminOverviewPage() {
  const navigate = useNavigate();
  const {
    users,
    experiences,
    adminActions,
    isLoading,
    error,
    fetchUsers,
    fetchExperiences,
    fetchAdminActions
  } = useAdminStore();

  // Calculate counts
  const creatorCount = users?.filter(user => user.role === 'creator').length || 0;
  const pendingExperiencesCount = experiences?.filter(exp => exp.booking_type === 'request').length || 0;

  const stats = [
    {
      label: 'Creators',
      value: creatorCount,
      change: '+5%',
      trend: 'up',
      icon: Users,
      color: 'bg-blue-500/20',
      textColor: 'text-blue-400',
      path: '/admin/users'
    },
    {
      label: 'Experience Requests',
      value: pendingExperiencesCount,
      change: '-3%',
      trend: 'down',
      icon: FileCheck,
      color: 'bg-green-500/20',
      textColor: 'text-green-400',
      path: '/admin/experiences'
    }
  ];

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-sand-400">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {stats.map((stat, idx) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-earth-800/50 p-6 rounded-lg flex items-center justify-between"
          >
            <div className={`rounded-full p-3 ${stat.color} ${stat.textColor}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="ml-4 flex-1">
              <div className="text-2xl font-bold text-sand-100">{stat.value}</div>
              <div className="text-sand-400 text-sm">{stat.label}</div>
            </div>
            <div className="flex flex-col items-end">
              <span className={`text-xs ${stat.trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>{stat.change}</span>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate(stat.path)}>
                View
              </Button>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Agentic/forward-looking: Add agentic quick actions, recent activity, and agent suggestions here */}
      <div className="bg-earth-800/50 p-6 rounded-lg mt-8">
        <h2 className="text-xl font-display mb-6">Quick Actions (Agentic)</h2>
        <div className="space-y-4">
          <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/admin/users')}>
            <Users className="h-4 w-4 mr-2" />
            Manage Users
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/admin/experiences')}>
            <FileCheck className="h-4 w-4 mr-2" />
            Review Experiences
          </Button>
        </div>
      </div>
      <div className="bg-earth-800/50 p-6 rounded-lg mt-8">
        <h2 className="text-xl font-display mb-6">Recent Activity</h2>
        <div className="space-y-2">
          {adminActions && adminActions.length > 0 ? (
            adminActions.slice(0, 5).map((action, idx) => (
              <div key={action.id} className="flex items-center gap-2 text-sand-300 text-sm">
                <span>{action.action_type}</span>
                <span className="text-sand-500">{action.target_type}</span>
                <span className="text-sand-400">{new Date(action.created_at).toLocaleDateString()}</span>
              </div>
            ))
          ) : (
            <div className="text-sand-400">No recent activity</div>
          )}
        </div>
      </div>
    </div>
  );
}